import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { reportCreatorI18n } from './i18n';
import { builderReducer, initialBuilderState } from './builderReducer';
import { buildQueryPayload, validateLocalBasics } from './builderValidation';
import { reportsBuilderApi } from '../../services/reportsBuilderService';
import { reportStorageService } from './reportStorageService';
import FieldsPanel from './components/FieldsPanel';
import SavedReportsList from './components/SavedReportsList';
import BuilderZone from './components/BuilderZone';
import ConfigPanel from './components/ConfigPanel';
import PreviewPanel from './components/PreviewPanel';
import './reportCreator.scss';


const SAVED_REPORTS_PAGE_SIZE = 20;

const parseDragPayload = (event) => {
  const raw = event.dataTransfer.getData('application/json');
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
};

const resolveChartSupport = (preview, definition) => {
  if (preview.status !== 'success') return false;
  if (!preview.rows.length || preview.columns.length < 2) return false;
  if (!definition.dimensions.length || !definition.metrics.length) return false;
  const metricAccessor = preview.columns[1]?.accessor;
  if (!metricAccessor) return false;
  return preview.rows.every((row) =>
    Number.isFinite(Number(row[metricAccessor]))
  );
};

const normalizeColumns = (columns) =>
  (Array.isArray(columns) ? columns : []).map((column) => {
    if (typeof column === 'string') {
      return { accessor: column, label: column };
    }
    return {
      accessor: column.accessor || column.key || column.name || '',
      label: column.label || column.name || column.accessor || '',
    };
  });

const createRuleFromField = (field, zone, existing) => {
  const base = {
    uiId:
      existing?.uiId ||
      `rule_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    fieldKey: field.fieldKey,
    label: field.label,
    entity: field.entity,
    entityKey: field.entityKey,
    dataType: field.dataType,
    selectable: Boolean(field.selectable),
    filterable: Boolean(field.filterable),
    groupable: Boolean(field.groupable),
    sortable: Boolean(field.sortable),
    supportsValueLookup: Boolean(field.supportsValueLookup),
    supportedOperators: field.supportedOperators || [],
    supportedAggregates: field.supportedAggregates || [],
    alias: existing?.alias || '',
  };

  if (zone === 'metrics') {
    return {
      ...base,
      aggregation:
        existing?.aggregation ||
        existing?.aggregate ||
        field.supportedAggregates?.[0] ||
        (field.dataType === 'number' ? 'sum' : 'count'),
      distinct: Boolean(existing?.distinct),
    };
  }

  if (zone === 'filters') {
    const normalizedValues = Array.isArray(existing?.values)
      ? existing.values
      : existing?.valueTo !== undefined
        ? [existing?.value, existing?.valueTo]
        : existing?.value !== undefined
          ? [existing.value]
          : [];

    return {
      ...base,
      operator: existing?.operator || field.supportedOperators?.[0] || 'eq',
      values: normalizedValues,
    };
  }

  if (zone === 'sort') {
    return {
      ...base,
      direction: existing?.direction || 'asc',
    };
  }

  return base;
};

const normalizeLoadedDefinition = (rawDefinition, fieldMap) => {
  const normalizeZone = (zone, items) =>
    (Array.isArray(items) ? items : []).map((item) => {
      const field = fieldMap.get(item.fieldKey);
      if (field) return createRuleFromField(field, zone, item);
      const fallbackField = {
        fieldKey: item.fieldKey || '',
        label: item.fieldKey || 'Campo',
        entity: 'General',
        entityKey: 'General',
        dataType: 'text',
        selectable: true,
        filterable: true,
        groupable: true,
        sortable: true,
        supportsValueLookup: false,
        supportedOperators: [],
        supportedAggregates: [],
      };
      return createRuleFromField(fallbackField, zone, item);
    });

  return {
    name: rawDefinition?.name || '',
    description: rawDefinition?.description || '',
    dimensions: normalizeZone('dimensions', rawDefinition?.dimensions),
    metrics: normalizeZone('metrics', rawDefinition?.metrics),
    filters: normalizeZone('filters', rawDefinition?.filters),
    sort: normalizeZone('sort', rawDefinition?.sort),
    page: Number(rawDefinition?.page || 1),
    pageSize: Number(rawDefinition?.pageSize || 100),
  };
};

const ReportCreator = ({ DesignSystemComponents }) => {
  const { Card, Button, Input } = DesignSystemComponents ?? {};
  const runAbortRef = useRef(null);

  const { language } = useLanguage();
  const rcLang = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: reportCreatorI18n });
  useEffect(() => {
    reportCreatorI18n.changeLanguage(rcLang);
  }, [rcLang]);

  const zoneConfig = useMemo(
    () => [
      {
        id: 'dimensions',
        title: t('rc_zone_dimensions_title'),
        helpText: t('rc_zone_dimensions_help'),
      },
      {
        id: 'metrics',
        title: t('rc_zone_metrics_title'),
        helpText: t('rc_zone_metrics_help'),
      },
      {
        id: 'filters',
        title: t('rc_zone_filters_title'),
        helpText: t('rc_zone_filters_help'),
      },
      {
        id: 'sort',
        title: t('rc_zone_sort_title'),
        helpText: t('rc_zone_sort_help'),
      },
    ],
    [t]
  );

  const [state, dispatch] = useReducer(builderReducer, initialBuilderState);
  const [fieldSearch, setFieldSearch] = useState('');
  const [fields, setFields] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [savedReports, setSavedReports] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [savedReportsPage, setSavedReportsPage] = useState(1);
  const [savedReportsTotal, setSavedReportsTotal] = useState(0);
  const [savedReportsSearch, setSavedReportsSearch] = useState('');
  const [pageError, setPageError] = useState('');
  const [chartView, setChartView] = useState('table');

  const fieldMap = useMemo(
    () => new Map(fields.map((field) => [field.fieldKey, field])),
    [fields]
  );

  const selectedChip = useMemo(() => {
    if (!state.selectedChip) return null;
    const zoneItems = state.definition[state.selectedChip.zone] || [];
    const item = zoneItems.find(
      (candidate) => candidate.uiId === state.selectedChip.uiId
    );
    if (!item) return null;
    return { ...item, zone: state.selectedChip.zone };
  }, [state.definition, state.selectedChip]);

  const chartSupported = useMemo(
    () => resolveChartSupport(state.preview, state.definition),
    [state.preview, state.definition]
  );

  useEffect(() => {
    if (!chartSupported && chartView !== 'table') {
      setChartView('table');
    }
  }, [chartSupported, chartView]);

  const loadFieldCatalog = async () => {
    try {
      setFieldsLoading(true);
      setPageError('');
      const response = await reportsBuilderApi.getCatalog();
      setFields(response.fields || []);
      dispatch({
        type: 'SET_OPERATOR_CATALOG',
        payload: response.operators || [],
      });
    } catch (error) {
      setPageError(
        error.customMessage || error.message || t('rc_error_load_catalog')
      );
    } finally {
      setFieldsLoading(false);
    }
  };

  const loadSavedReports = useCallback(async () => {
    try {
      setSavedLoading(true);
      const response = await reportStorageService.list({
        page: savedReportsPage,
        pageSize: SAVED_REPORTS_PAGE_SIZE,
        search: savedReportsSearch,
      });
      setSavedReports(response.items || []);
      setSavedReportsTotal(Number(response.total || 0));
    } catch (error) {
      setPageError(
        error.customMessage || error.message || t('rc_error_load_reports')
      );
    } finally {
      setSavedLoading(false);
    }
  }, [savedReportsPage, savedReportsSearch, t]);

  useEffect(() => {
    loadFieldCatalog();
  }, []);

  useEffect(() => {
    loadSavedReports();
  }, [loadSavedReports]);

  useEffect(() => {
    return () => {
      if (runAbortRef.current) {
        runAbortRef.current.abort();
      }
    };
  }, []);

  const handleDropField = (event, zone) => {
    event.preventDefault();
    const payload = parseDragPayload(event);
    if (!payload) return;

    if (payload.source === 'catalog') {
      const field = fieldMap.get(payload.fieldKey);
      if (!field) return;

      if (zone === 'dimensions' && !field.groupable) {
        dispatch({
          type: 'SET_ZONE_ERROR',
          payload: {
            zone,
            message: t('rc_error_field_not_groupable'),
          },
        });
        return;
      }

      if (
        zone === 'metrics' &&
        (!field.supportedAggregates || !field.supportedAggregates.length)
      ) {
        dispatch({
          type: 'SET_ZONE_ERROR',
          payload: {
            zone,
            message: t('rc_error_field_not_aggregable'),
          },
        });
        return;
      }

      if (zone === 'filters' && !field.filterable) {
        dispatch({
          type: 'SET_ZONE_ERROR',
          payload: {
            zone,
            message: t('rc_error_field_not_filterable'),
          },
        });
        return;
      }

      if (
        zone === 'sort' &&
        (!field.sortable ||
          (!state.definition.dimensions.some(
            (item) => item.fieldKey === field.fieldKey
          ) &&
            !state.definition.metrics.some(
              (item) => item.fieldKey === field.fieldKey
            )))
      ) {
        dispatch({
          type: 'SET_ZONE_ERROR',
          payload: {
            zone,
            message: t('rc_error_field_not_sortable'),
          },
        });
        return;
      }

      dispatch({
        type: 'DROP_NEW_FIELD',
        payload: { field, zone },
      });
      return;
    }

    if (payload.source === 'zone' && payload.zone === zone) {
      dispatch({
        type: 'REORDER_ZONE',
        payload: {
          zone,
          sourceIndex: payload.index,
          targetIndex: (state.definition[zone] || []).length - 1,
        },
      });
    }
  };

  const handleDragZoneChipStart = (event, zone, index) => {
    event.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        source: 'zone',
        zone,
        index,
      })
    );
  };

  const handleDropZoneChip = (event, zone, targetIndex) => {
    event.preventDefault();
    const payload = parseDragPayload(event);
    if (!payload || payload.source !== 'zone' || payload.zone !== zone) return;
    dispatch({
      type: 'REORDER_ZONE',
      payload: { zone, sourceIndex: payload.index, targetIndex },
    });
  };

  const handleMoveChip = (zone, sourceIndex, targetIndex) => {
    dispatch({
      type: 'REORDER_ZONE',
      payload: { zone, sourceIndex, targetIndex },
    });
  };

  const handleUpdateSelectedChip = (updates) => {
    if (!selectedChip) return;
    dispatch({
      type: 'UPDATE_CHIP',
      payload: {
        zone: selectedChip.zone,
        uiId: selectedChip.uiId,
        updates,
      },
    });
  };

  const handleSaveReport = async () => {
    setPageError('');
    if (!state.definition.name.trim()) {
      setPageError(t('rc_error_name_required'));
      return;
    }

    try {
      await reportStorageService.save(state.definition, state.operatorCatalog);
      if (savedReportsPage === 1) {
        await loadSavedReports();
      } else {
        setSavedReportsPage(1);
      }
    } catch (error) {
      setPageError(
        error.customMessage || error.message || t('rc_error_save_report')
      );
    }
  };

  const handleLoadReport = async (reportId) => {
    setPageError('');
    try {
      const report = await reportStorageService.load(reportId);
      const normalizedDefinition = normalizeLoadedDefinition(
        {
          ...report.definition,
          name: report.name,
          description: report.description,
        },
        fieldMap
      );
      dispatch({
        type: 'LOAD_REPORT',
        payload: {
          definition: normalizedDefinition,
        },
      });
      setChartView('table');
    } catch (error) {
      setPageError(
        error.customMessage || error.message || t('rc_error_load_report')
      );
    }
  };

  const handleRequestFieldValues = useCallback(
    (fieldKey, search, limit) =>
      reportsBuilderApi.getFieldValues(fieldKey, search, limit),
    []
  );

  const handleRunReport = async () => {
    setPageError('');
    dispatch({ type: 'RESET_PREVIEW' });

    const basics = validateLocalBasics(state.definition, state.operatorCatalog);
    if (!basics.isValid) {
      dispatch({
        type: 'SET_VALIDATION',
        payload: basics,
      });
      dispatch({
        type: 'SET_PREVIEW',
        payload: {
          status: 'invalid',
        },
      });
      return;
    }

    dispatch({
      type: 'SET_VALIDATION',
      payload: basics,
    });

    if (runAbortRef.current) runAbortRef.current.abort();
    runAbortRef.current = new AbortController();

    const queryPayload = buildQueryPayload(
      state.definition,
      state.operatorCatalog
    );

    dispatch({
      type: 'SET_PREVIEW',
      payload: {
        status: 'loading',
        columns: [],
        rows: [],
        errorMessage: '',
        recordsFound: 0,
        rowCount: 0,
        executionMs: 0,
      },
    });

    try {
      const response = await reportsBuilderApi.query(
        queryPayload,
        runAbortRef.current.signal
      );
      const columns = normalizeColumns(response.columns);
      const rows = Array.isArray(response.data) ? response.data : [];
      const status = !columns.length
        ? 'empty'
        : rows.length
          ? 'success'
          : 'no-data';

      dispatch({
        type: 'SET_PREVIEW',
        payload: {
          status,
          columns,
          rows,
          errorMessage: '',
          recordsFound: response.recordsFound || 0,
          rowCount: response.rowCount || 0,
          executionMs: response.executionMs || 0,
        },
      });
    } catch (error) {
      if (
        error?.name === 'CanceledError' ||
        error?.code === 'ERR_CANCELED' ||
        error?.isClientCanceled
      ) {
        dispatch({
          type: 'SET_PREVIEW',
          payload: {
            status: 'idle',
            errorMessage: '',
          },
        });
        return;
      }

      dispatch({
        type: 'SET_PREVIEW',
        payload: {
          status: 'error',
          columns: [],
          rows: [],
          errorMessage:
            error.customMessage ||
            error.message ||
            t('rc_error_run_query'),
        },
      });
    } finally {
      runAbortRef.current = null;
    }
  };

  const handleCancelRun = () => {
    if (runAbortRef.current) {
      runAbortRef.current.abort();
    }
  };

  if (!Card || !Button || !Input) {
    return null;
  }

  return (
    <div className='report-creator-page'>
      <div className='mb-4'>
        <h2 className='fw-semibold'>{t('rc_page_title')}</h2>
        <h4 className='fw-normal'>{t('rc_page_subtitle')}</h4>
      </div>

      {pageError && (
        <Card specialPadding='px-4 py-3' className='report-error-banner mb-3'>
          <p className='mb-0 text-danger'>{pageError}</p>
        </Card>
      )}

      {state.warnings.length > 0 && (
        <Card specialPadding='px-4 py-3' className='report-warning-banner mb-3'>
          {state.warnings.map((warning, idx) => (
            <p key={`${warning}-${idx}`} className='mb-1'>
              {warning}
            </p>
          ))}
        </Card>
      )}

      <div className='report-creator-stack'>
        <div className='report-builder-grid'>
          <aside className='report-builder-sidebar'>
            <FieldsPanel
              DesignSystemComponents={DesignSystemComponents}
              search={fieldSearch}
              onSearchChange={setFieldSearch}
              fields={fields}
            />

            <SavedReportsList
              DesignSystemComponents={DesignSystemComponents}
              reports={savedReports}
              loading={savedLoading}
              onLoadReport={handleLoadReport}
              page={savedReportsPage}
              pageSize={SAVED_REPORTS_PAGE_SIZE}
              total={savedReportsTotal}
              search={savedReportsSearch}
              onSearchChange={(value) => {
                setSavedReportsSearch(value);
                setSavedReportsPage(1);
              }}
              onPageChange={setSavedReportsPage}
            />

            {fieldsLoading && (
              <p className='caption color-gray-600 mt-2 mb-0'>
                {t('rc_loading_catalog')}
              </p>
            )}
          </aside>

          <section className='report-builder-workspace'>
            <Card specialPadding='px-4 pt-4 pb-3' className='report-setup-card'>
              <div className='report-setup-row'>
                <div className='report-setup-item report-setup-item-name'>
                  <label className='form-label'>{t('rc_report_name_label')}</label>
                  <Input
                    placeholder={t('rc_report_name_placeholder')}
                    value={state.definition.name}
                    onChange={(event) =>
                      dispatch({
                        type: 'SET_DEFINITION_KEY',
                        payload: { key: 'name', value: event.target.value },
                      })
                    }
                  />
                </div>
                <div className='report-setup-item report-setup-item-description'>
                  <label className='form-label'>{t('rc_description_label')}</label>
                  <Input
                    placeholder={t('rc_description_placeholder')}
                    value={state.definition.description}
                    onChange={(event) =>
                      dispatch({
                        type: 'SET_DEFINITION_KEY',
                        payload: {
                          key: 'description',
                          value: event.target.value,
                        },
                      })
                    }
                  />
                </div>
                <div className='report-setup-item report-setup-item-actions'>
                  <Button
                    label={t('rc_btn_save')}
                    variant='secondary'
                    size='sm'
                    onClick={handleSaveReport}
                  />
                </div>
              </div>
            </Card>

            <Card
              specialPadding='px-4 pt-4 pb-4'
              className='report-canvas-card mt-3'
            >
              {state.summaryErrors.length > 0 && (
                <div className='report-summary-errors mb-3'>
                  {state.summaryErrors.map((error, idx) => (
                    <p key={`${error}-${idx}`} className='mb-1'>
                      {error}
                    </p>
                  ))}
                </div>
              )}

              <div className='builder-zones'>
                {zoneConfig.map((zone) => (
                  <BuilderZone
                    key={zone.id}
                    DesignSystemComponents={DesignSystemComponents}
                    id={zone.id}
                    title={zone.title}
                    helpText={zone.helpText}
                    items={state.definition[zone.id]}
                    error={state.zoneErrors[zone.id]}
                    onDropField={handleDropField}
                    onSelectChip={(selection) =>
                      dispatch({ type: 'SELECT_CHIP', payload: selection })
                    }
                    selectedChip={state.selectedChip}
                    onRemoveChip={(zoneId, uiId) =>
                      dispatch({
                        type: 'REMOVE_CHIP',
                        payload: { zone: zoneId, uiId },
                      })
                    }
                    onDragZoneChipStart={handleDragZoneChipStart}
                    onDropZoneChip={handleDropZoneChip}
                    onMoveChip={handleMoveChip}
                  />
                ))}
              </div>

              {selectedChip && (
                <ConfigPanel
                  DesignSystemComponents={DesignSystemComponents}
                  selectedChip={selectedChip}
                  onUpdateChip={handleUpdateSelectedChip}
                  onRequestFieldValues={handleRequestFieldValues}
                  operatorCatalog={state.operatorCatalog}
                  className='report-config-strip mt-3'
                  onClose={() =>
                    dispatch({ type: 'SELECT_CHIP', payload: null })
                  }
                />
              )}
            </Card>
          </section>
        </div>

        <PreviewPanel
          DesignSystemComponents={DesignSystemComponents}
          preview={state.preview}
          chartView={chartView}
          onChartViewChange={setChartView}
          chartSupported={chartSupported}
          definition={state.definition}
          isRunning={state.preview.status === 'loading'}
          onRunReport={handleRunReport}
          onCancelRun={handleCancelRun}
        />
      </div>
    </div>
  );
};

export default ReportCreator;
